import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatBRL, formatApiError, formatDateTimeBR, fileUrl } from "../lib/api";
import { processAndUploadImage } from "../lib/imageUpload";
import { isNative, openPdfFile } from "../lib/native";
import { PhotoSourceSheet } from "../components/PhotoSourceSheet";
import usePullToRefresh from "../lib/usePullToRefresh";
import PullIndicator from "../components/PullIndicator";
import { getClientIp } from "../lib/clientIp";
import { toast } from "sonner";
import {
  Scales, Warning, ShieldWarning, CheckCircle, XCircle, ArrowLeft, UploadSimple,
  Camera, FileText, CurrencyCircleDollar, Gavel, IdentificationCard, Prohibit, Clock, Spinner, FileArrowDown,
} from "@phosphor-icons/react";
import AsaasCardForm from "../components/AsaasCardForm";

const STATUS_LABEL = {
  open: { t: "Aguardando sua resposta", c: "bg-amber-100 text-amber-800" },
  admin_review: { t: "Em análise do AlugaJá", c: "bg-blue-100 text-blue-800" },
  contested: { t: "Contestada — em análise", c: "bg-blue-100 text-blue-800" },
  awaiting_quotes: { t: "Aguardando orçamentos", c: "bg-amber-100 text-amber-800" },
  awaiting_choice: { t: "Escolha um orçamento", c: "bg-amber-100 text-amber-800" },
  awaiting_payment: { t: "Aguardando pagamento", c: "bg-orange-100 text-orange-800" },
  awaiting_settlement: { t: "Acordo aceito — pagamento pendente", c: "bg-orange-100 text-orange-800" },
  bo_released: { t: "Dossiê liberado", c: "bg-purple-100 text-purple-800" },
  resolved: { t: "Resolvida", c: "bg-emerald-100 text-emerald-800" },
  closed: { t: "Encerrada pela mediação", c: "bg-slate-200 text-slate-700" },
  defaulted: { t: "Inadimplente", c: "bg-red-100 text-red-800" },
  expired: { t: "Encerrada por prazo", c: "bg-slate-200 text-slate-700" },
};

// Estados ativos onde qualquer parte pode propor o encerramento amigável
const PROPOSAL_STATES = ["open", "admin_review", "contested", "awaiting_quotes", "awaiting_choice", "awaiting_payment"];
const TERMINAL_STATES = ["resolved", "closed", "defaulted", "expired"];
const SUPPORT_EMAIL = "suporte@alugaja.app.br";

function StatusBadge({ status }) {
  const m = STATUS_LABEL[status] || { t: status, c: "bg-slate-100 text-slate-700" };
  return <span className={`text-[11px] font-bold px-2 py-1 rounded-full ${m.c}`} data-testid="dispute-status-badge">{m.t}</span>;
}

// Bloco legal de compliance — sempre visível para ambas as partes
function ComplianceBanner() {
  return (
    <div className="rounded-xl border-2 border-red-200 bg-red-50 p-3 text-[11px] leading-relaxed text-red-900" data-testid="compliance-banner">
      <div className="flex items-center gap-1.5 font-black text-red-700 mb-1">
        <ShieldWarning size={16} weight="fill" /> TERMO DE COMPLIANCE E SEGURANÇA — ALUGAJÁ
      </div>
      <p className="mb-1"><b>Locador:</b> Inflar orçamentos de má-fé ou simular defeitos configura <b>Estelionato (Art. 171 do CP)</b>, passível de banimento e responsabilização jurídica.</p>
      <p className="mb-1"><b>Locatário:</b> A não devolução do bem configura <b>Apropriação Indébita (Art. 168 do CP)</b>. O aceite do dano sem o devido pagamento resultará no <b>bloqueio imediato das funções do app</b>, registro de reincidência e fornecimento de dados para cobrança e medidas cíveis/criminais fora da plataforma.</p>
      <p className="text-red-700/80">O AlugaJá apenas <b>facilita o contato</b> entre as partes — a decisão final é um acordo entre os envolvidos.</p>
    </div>
  );
}

// Diálogo de confirmação que deixa explícito o que a ação implica
function ConfirmDialog({ open, title, body, confirmLabel, danger, onConfirm, onClose, busy }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-end md:items-center md:justify-center pb-safe" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="dispute-confirm-dialog">
      <div className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-2xl p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="font-heading font-black text-lg">{title}</div>
        <div className="text-sm text-slate-600">{body}</div>
        <div className="text-[11px] text-slate-400">Ao confirmar, você concorda com este ato. A AlugaJá apenas facilita o contato; a decisão é um acordo entre as partes.</div>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={onClose} className="btn-ghost" data-testid="confirm-cancel">Cancelar</button>
          <button onClick={onConfirm} disabled={busy} className={`${danger ? "bg-red-600 hover:bg-red-700" : "btn-primary"} text-white font-bold rounded-xl py-2.5 disabled:opacity-50`} data-testid="confirm-ok">
            {busy ? "Processando..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Lista de disputas ───────────────────────────────────────────────────────
function DisputeList() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const loadList = useCallback(() => api.get("/disputes/mine").then((r) => setItems(r.data)).catch(() => setItems([])), []);
  useEffect(() => { loadList(); }, [loadList]);
  const ptr = usePullToRefresh(loadList);
  return (
    <div className="max-w-3xl mx-auto px-4 py-5" data-testid="dispute-list-page">
      <PullIndicator pulling={ptr.isPulling} progress={ptr.progress} refreshing={ptr.isRefreshing} />
      <button onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-slate-500 font-bold mb-3" data-testid="dispute-list-back">
        <ArrowLeft size={16} weight="bold" /> Voltar
      </button>
      <div className="flex items-center gap-2 mb-4">
        <Scales size={26} weight="fill" className="text-[#004E98]" />
        <h1 className="font-heading font-black text-2xl">Central de Disputas</h1>
      </div>
      <ComplianceBanner />
      <div className="mt-4 space-y-3">
        {items == null ? (
          <div className="aj-card p-8 text-center text-slate-400">Carregando...</div>
        ) : items.length === 0 ? (
          <div className="aj-card p-10 text-center text-slate-500" data-testid="dispute-list-empty">Você não tem disputas.</div>
        ) : items.map((d) => (
          <Link key={d.id} to={`/disputas/${d.id}`} className="aj-card p-4 flex items-center justify-between hover:shadow-md transition-shadow" data-testid={`dispute-row-${d.id}`}>
            <div>
              <div className="font-bold text-sm">{d.listing_title || "Locação"}</div>
              <div className="text-xs text-slate-500">
                {d.dispute_type === "not_returned" ? "Não devolvido" : "Dano"} · {d.is_owner ? "Você abriu" : "Aberta pelo dono"} · {formatDateTimeBR(d.created_at)}
              </div>
              {d.needs_my_action && (
                <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5" data-testid={`dispute-action-needed-${d.id}`}>
                  {d.proposal_pending_for_me ? "📩 Proposta de acordo — responda" : "⚠️ Precisa da sua ação"}
                </div>
              )}
            </div>
            <StatusBadge status={d.status} />
          </Link>
        ))}
      </div>
    </div>
  );
}

// ── Detalhe da disputa (multi-fase) ─────────────────────────────────────────
function DisputeDetail({ id }) {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);  // {title, body, confirmLabel, danger, action}

  const load = useCallback(() => api.get(`/disputes/${id}`).then((r) => setD(r.data)).catch((e) => {
    toast.error(formatApiError(e.response?.data?.detail)); nav("/disputas");
  }), [id, nav]);
  useEffect(() => { load(); }, [load]);

  if (!d) return <div className="max-w-3xl mx-auto px-4 py-10 text-center text-slate-400" data-testid="dispute-loading">Carregando disputa...</div>;

  const runConfirm = async () => {
    if (!confirm?.action) return;
    setBusy(true);
    try { await confirm.action(); setConfirm(null); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-5 space-y-4" data-testid="dispute-detail-page">
      <button onClick={() => nav("/disputas")} className="flex items-center gap-1 text-sm text-slate-500 font-bold" data-testid="dispute-back">
        <ArrowLeft size={16} weight="bold" /> Disputas
      </button>

      <div className="aj-card p-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="font-heading font-black text-lg">{d.listing_title || "Locação"}</div>
            <div className="text-xs text-slate-500">
              {d.dispute_type === "not_returned" ? "🚫 Item não devolvido" : "🛠️ Dano relatado"} · Disputa #{id.slice(-6)}
            </div>
          </div>
          {d.proposal_pending_for_me && !TERMINAL_STATES.includes(d.status) ? (
            <span className="text-[11px] font-bold px-2 py-1 rounded-full bg-emerald-100 text-emerald-800" data-testid="dispute-status-badge">📩 Proposta de acordo</span>
          ) : (
            <StatusBadge status={d.status} />
          )}
        </div>
        <div className="mt-3 text-sm">
          <div className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Motivo</div>
          <div className="font-semibold">{d.reason}</div>
          {d.description && <div className="text-xs text-slate-600 mt-1">{d.description}</div>}
        </div>
        {Array.isArray(d.photos) && d.photos.length > 0 && (
          <div className="mt-3" data-testid="dispute-photos">
            <div className="text-[10px] uppercase font-bold tracking-wider text-slate-400 mb-1">Fotos do estrago</div>
            <div className="flex gap-2 overflow-x-auto">
              {d.photos.map((p, i) => (
                <a key={i} href={fileUrl(p)} target="_blank" rel="noreferrer" className="shrink-0">
                  <img src={fileUrl(p)} alt="" className="w-24 h-24 object-cover rounded-lg border border-slate-200" />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      <ComplianceBanner />

      <PhaseBlock d={d} id={id} reload={load} setConfirm={setConfirm} />

      <DossieDownload d={d} id={id} />

      <ResolutionBox d={d} id={id} reload={load} />

      <ConfirmDialog
        open={!!confirm} title={confirm?.title} body={confirm?.body}
        confirmLabel={confirm?.confirmLabel} danger={confirm?.danger}
        onConfirm={runConfirm} onClose={() => setConfirm(null)} busy={busy}
      />
    </div>
  );
}

// Renderiza o bloco de ação conforme a fase + papel do usuário
function PhaseBlock({ d, id, reload, setConfirm }) {
  // Fase 1 — defesa do locatário (dano)
  if (d.dispute_type === "damage" && d.status === "open" && d.is_renter) {
    return (
      <div className="aj-card p-4 space-y-3" data-testid="phase-defense">
        <div className="font-bold text-sm">Você reconhece o dano relatado?</div>
        <p className="text-xs text-slate-500">Veja as fotos acima. Se reconhecer, o dono enviará 3 orçamentos e você escolhe o conserto. Se contestar, o caso vai para análise humana do AlugaJá.</p>
        <DeadlineNote deadline={d.defense_deadline} label="Prazo para responder" />
        <div className="grid grid-cols-1 gap-2">
          <button
            data-testid="defense-accept-btn"
            onClick={() => setConfirm({
              title: "Reconheço que estraguei",
              body: "Você assume a responsabilidade pelo dano. O dono enviará 3 orçamentos e você deverá pagar o conserto escolhido. Não pagar dentro do prazo bloqueia as funções do seu app.",
              confirmLabel: "Sim, reconheço",
              action: () => api.post(`/disputes/${id}/defense`, { action: "accept" }),
            })}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl py-3 flex items-center justify-center gap-2">
            <CheckCircle size={18} weight="fill" /> Eu reconheço que estraguei
          </button>
          <button
            data-testid="defense-contest-btn"
            onClick={() => setConfirm({
              title: "Contestar a disputa",
              body: "O caso será enviado para análise humana do AlugaJá. Inflar/forjar provas pode configurar crime. O pagamento permanece bloqueado até a mediação.",
              confirmLabel: "Contestar (ir para análise)", danger: true,
              action: () => api.post(`/disputes/${id}/defense`, { action: "contest" }),
            })}
            className="bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl py-3 flex items-center justify-center gap-2">
            <XCircle size={18} weight="fill" /> Contestar disputa
          </button>
        </div>
      </div>
    );
  }

  if (d.dispute_type === "damage" && d.status === "open" && d.is_owner) {
    return <InfoCard icon={Clock} text="Aguardando o locatário responder (reconhecer ou contestar o dano)." />;
  }
  if (d.status === "contested") {
    return <InfoCard icon={Gavel} text="O locatário contestou. O caso está em análise humana do AlugaJá. Você será avisado da decisão." />;
  }
  if (d.status === "admin_review") {
    return <InfoCard icon={Gavel} text={d.dispute_type === "not_returned" ? "Caso de não-devolução em análise do AlugaJá." : "Disputa em análise do AlugaJá."} />;
  }

  // Fase 2 — orçamentos
  if (d.status === "awaiting_quotes" && d.is_owner) {
    return <QuotesForm id={id} reload={reload} deadline={d.quote_deadline} />;
  }
  if (d.status === "awaiting_quotes" && d.is_renter) {
    return <InfoCard icon={Clock} text="Você reconheceu o dano. Aguardando o dono enviar os 3 orçamentos." />;
  }

  // Fase 3 — escolha + pagamento (locatário)
  if (d.status === "awaiting_choice" && d.is_renter) {
    return <ChooseAndPay d={d} id={id} reload={reload} />;
  }
  if (d.status === "awaiting_choice" && d.is_owner) {
    return <InfoCard icon={Clock} text="Orçamentos enviados. Aguardando o locatário escolher e pagar o conserto." />;
  }

  if (d.status === "awaiting_settlement") {
    if (d.is_renter) return <SettlementPay d={d} id={id} reload={reload} />;
    return <InfoCard icon={Clock} text={`Acordo de ${formatBRL(d.settlement?.amount || 0)} aceito. Aguardando o pagamento do locatário.`} />;
  }

  if (d.status === "awaiting_payment") {
    if (d.is_renter) return <RepairPayment d={d} id={id} reload={reload} />;
    return <InfoCard icon={Clock} text="O locatário escolheu um orçamento. Aguardando a confirmação do pagamento do conserto." />;
  }

  // Inadimplência
  if (d.status === "defaulted") {
    return (
      <div className="aj-card p-4 border-2 border-red-300 bg-red-50" data-testid="phase-defaulted">
        <div className="flex items-center gap-2 font-black text-red-700"><Prohibit size={20} weight="fill" /> Conta com restrição</div>
        {d.is_renter ? (
          <p className="text-sm text-red-900 mt-2">Você reconheceu o dano mas não pagou o conserto no prazo. As funções do app estão <b>bloqueadas</b>. Regularize com o dono e o suporte AlugaJá para reativar sua conta.</p>
        ) : (
          <p className="text-sm text-red-900 mt-2">O locatário não pagou o conserto no prazo e foi marcado como inadimplente. O AlugaJá registrou a reincidência e forneceu os dados para as medidas cabíveis.</p>
        )}
      </div>
    );
  }

  // Dossiê de B.O. (não devolução aprovada)
  if (d.dispute_type === "not_returned" && d.status === "bo_released") {
    if (d.is_owner) return <BoDossier id={id} />;
    return <InfoCard icon={Gavel} text="O AlugaJá aprovou o caso de não-devolução. Os dados foram liberados ao dono para registro de B.O." />;
  }

  // Encerrada por prazo (SLA) — orientação de fallback (B.O. via suporte)
  if (d.status === "expired") {
    return (
      <div className="aj-card p-4 border-2 border-slate-300 bg-slate-50 space-y-2" data-testid="phase-expired">
        <div className="flex items-center gap-2 font-black text-slate-700"><Clock size={20} weight="fill" /> Disputa encerrada por prazo</div>
        <p className="text-sm text-slate-700">
          O tempo para resolver esta disputa pela plataforma encerrou
          {d.expired_from === "awaiting_quotes" ? " (o dono não enviou os orçamentos no prazo)." : " (não houve resposta no prazo)."}
        </p>
        <div className="rounded-xl border border-slate-300 bg-white p-3 text-sm text-slate-700" data-testid="expired-fallback">
          Para prosseguir, envie um e-mail para <b>{SUPPORT_EMAIL}</b> com o assunto{" "}
          <b>DISPUTA NÃO RESOLVIDA</b> solicitando os dados da locação (B.O.) e as medidas cabíveis.
          A AlugaJá apenas facilita o contato entre as partes.
        </div>
      </div>
    );
  }

  if (d.status === "resolved") {
    return (
      <div className="aj-card p-4 border-2 border-emerald-200 bg-emerald-50" data-testid="phase-resolved">
        <div className="flex items-center gap-2 font-black text-emerald-700"><CheckCircle size={20} weight="fill" /> Disputa resolvida</div>
        <p className="text-sm text-emerald-900 mt-1">
          {d.repair?.status === "paid"
            ? `O conserto foi pago. ${d.is_owner ? `R$ ${formatBRL(d.repair.owner_credit)} creditado na sua Carteira AlugaJá.` : "O valor foi creditado ao dono."}`
            : "O caso foi encerrado pela mediação do AlugaJá."}
        </p>
      </div>
    );
  }

  if (d.status === "closed") {
    return (
      <div className="aj-card p-4 border-2 border-slate-300 bg-slate-50" data-testid="phase-closed">
        <div className="flex items-center gap-2 font-black text-slate-700"><CheckCircle size={20} weight="fill" /> Disputa encerrada</div>
        <p className="text-sm text-slate-700 mt-1">
          Esta disputa foi <b>encerrada pela mediação do AlugaJá</b>.{d.note ? ` Observação: ${d.note}` : ""}
        </p>
      </div>
    );
  }

  return <InfoCard icon={Clock} text="Acompanhe o andamento desta disputa por aqui." />;
}

function DossieDownload({ d, id }) {
  const [busy, setBusy] = useState(false);
  const released = d.dossie_released_to || [];
  const canSee = (d.is_owner && released.includes("owner")) || (d.is_renter && released.includes("renter"));
  if (!canSee) return null;

  const download = async () => {
    const native = isNative();
    const w = (!native) ? window.open("", "_blank") : null;
    setBusy(true);
    try {
      const r = await api.get(`/disputes/${id}/dossie-pdf`, { responseType: "blob", timeout: 120000 });
      const blob = new Blob([r.data], { type: "application/pdf" });
      if (native) {
        const res = await openPdfFile({ blob, fileName: `dossie_${id}.pdf` });
        if (!res.ok && res.method === "none") toast.error("Não foi possível abrir o PDF.");
        return;
      }
      const url = URL.createObjectURL(blob);
      if (w) w.location.href = url; else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (w) try { w.close(); } catch { /* noop */ }
      toast.error(formatApiError(e.response?.data?.detail) || "Erro ao abrir o dossiê");
    } finally { setBusy(false); }
  };

  return (
    <div className="aj-card p-4 border-2 border-purple-200 bg-purple-50 space-y-2" data-testid="dossie-download">
      <div className="flex items-center gap-2 font-black text-purple-700">
        <IdentificationCard size={20} weight="fill" /> Dossiê da disputa liberado
      </div>
      <p className="text-sm text-purple-900">A mediação do AlugaJá liberou o dossiê completo desta disputa para você. Ele também foi enviado ao seu e-mail.</p>
      <button onClick={download} disabled={busy} className="btn-primary w-full disabled:opacity-50" data-testid="dossie-download-btn">
        <FileArrowDown size={16} weight="bold" /> {busy ? "Abrindo..." : "Ver / Baixar dossiê (PDF)"}
      </button>
    </div>
  );
}

function InfoCard({ icon: Icon, text }) {
  return (
    <div className="aj-card p-4 flex items-start gap-2 text-sm text-slate-600" data-testid="phase-info">
      <Icon size={20} weight="bold" className="text-[#004E98] shrink-0 mt-0.5" />
      <span>{text}</span>
    </div>
  );
}

// Aviso de prazo (SLA) — vence à meia-noite
function DeadlineNote({ deadline, label }) {
  if (!deadline) return null;
  return (
    <div className="flex items-center gap-1.5 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5" data-testid="dispute-deadline">
      <Clock size={14} weight="bold" /> {label}: até {formatDateTimeBR(deadline)}
    </div>
  );
}

// Thread de negociação (histórico de propostas/contrapropostas)
function NegotiationThread({ history, d }) {
  if (!Array.isArray(history) || history.length === 0) return null;
  const nameFor = (uid, fallback) => (uid === d.owner_id ? "Locador" : uid === d.renter_id ? "Locatário" : (fallback || "—"));
  const label = { proposal: "propôs", counter: "contrapropôs", accepted: "aceitou", declined: "recusou" };
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-1.5" data-testid="negotiation-thread">
      <div className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Histórico da negociação</div>
      {history.map((h, i) => (
        <div key={i} className="text-xs text-slate-700 flex items-start gap-1.5" data-testid={`negotiation-item-${i}`}>
          <span className="text-slate-400">•</span>
          <span>
            <b>{nameFor(h.by_user_id, h.by_name)}</b> {label[h.event] || h.event}
            {typeof h.amount === "number" && h.amount > 0 ? <> <b>{formatBRL(h.amount)}</b></> : (h.event === "proposal" || h.event === "counter" ? " (sem valor)" : "")}
            {h.note ? <span className="italic text-slate-500"> — "{h.note}"</span> : ""}
          </span>
        </div>
      ))}
    </div>
  );
}

// "Resolvemos a disputa" — acordo amigável, com ou sem valor (V16: negociação + réplica)
function ResolutionBox({ d, id, reload }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [amount, setAmount] = useState("");
  const [showCounter, setShowCounter] = useState(false);
  const [counterAmount, setCounterAmount] = useState("");
  const [counterNote, setCounterNote] = useState("");
  const proposal = d.resolution_proposal;
  const active = PROPOSAL_STATES.includes(d.status);
  const history = d.negotiation_history;
  if (TERMINAL_STATES.includes(d.status)) return null;
  if (!active && !proposal) return null;

  const call = async (path, body) => {
    setBusy(true);
    try {
      await api.post(`/disputes/${id}/${path}`, body);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const parseAmt = (v) => {
    const n = parseFloat(String(v).replace(",", "."));
    return isNaN(n) || n < 0 ? 0 : Math.round(n * 100) / 100;
  };

  // Há uma proposta pendente
  if (proposal) {
    const amt = Number(proposal.amount || 0);
    if (d.proposal_is_mine) {
      return (
        <div className="aj-card p-4 border-2 border-[#004E98]/30 bg-[#004E98]/5 space-y-2" data-testid="resolution-pending-mine">
          <div className="flex items-center gap-2 font-bold text-sm text-[#004E98]"><Spinner size={18} className="animate-spin" /> Aguardando a outra parte</div>
          <p className="text-xs text-slate-600">
            Você propôs {amt > 0 ? <>um acordo de <b>{formatBRL(amt)}</b> (o locatário paga o locador)</> : "encerrar a disputa sem valor"}. A outra parte precisa aceitar, contrapropor ou recusar.
          </p>
          <NegotiationThread history={history} d={d} />
        </div>
      );
    }
    // Sou quem deve responder
    return (
      <div className="aj-card p-4 border-2 border-emerald-200 bg-emerald-50 space-y-3" data-testid="resolution-respond">
        <div className="flex items-center gap-2 font-bold text-sm text-emerald-800"><CheckCircle size={18} weight="fill" /> Proposta de acordo recebida</div>
        <div className="rounded-xl bg-white border border-emerald-200 p-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-slate-600">Valor proposto</span>
            <b className="font-heading text-lg text-[#004E98]" data-testid="proposal-amount">{amt > 0 ? formatBRL(amt) : "Sem valor"}</b>
          </div>
          {amt > 0 && <div className="text-[11px] text-slate-500 mt-1">Se aceitar, <b>você (locatário) paga</b> este valor e ele cai na carteira do locador. A disputa é encerrada.</div>}
          {proposal.note && <p className="text-xs italic text-slate-600 mt-2 border-t border-slate-100 pt-2">"{proposal.note}"</p>}
        </div>
        <NegotiationThread history={history} d={d} />
        {!showCounter ? (
          <div className="grid grid-cols-3 gap-2">
            <button onClick={() => call("resolve-response", { action: "decline" })} disabled={busy}
              className="btn-ghost text-sm disabled:opacity-50" data-testid="resolution-decline-btn">Recusar</button>
            <button onClick={() => setShowCounter(true)} disabled={busy}
              className="border-2 border-[#004E98] text-[#004E98] font-bold rounded-xl py-2.5 text-sm disabled:opacity-50" data-testid="resolution-counter-btn">Contrapropor</button>
            <button onClick={() => call("resolve-response", { action: "accept" })} disabled={busy}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl py-2.5 text-sm disabled:opacity-50" data-testid="resolution-accept-btn">
              {busy ? "..." : "Aceitar"}
            </button>
          </div>
        ) : (
          <div className="space-y-2 rounded-xl border-2 border-[#004E98]/30 p-3" data-testid="counter-form">
            <div className="text-xs font-bold text-[#004E98]">Sua contraproposta</div>
            <input type="number" step="0.01" min="0" inputMode="decimal" value={counterAmount}
              onChange={(e) => setCounterAmount(e.target.value)} placeholder="Valor (R$) — 0 para sem valor"
              className="aj-input" data-testid="counter-amount" />
            <textarea value={counterNote} onChange={(e) => setCounterNote(e.target.value)} rows={2}
              placeholder="Mensagem (opcional)" className="aj-input text-sm" data-testid="counter-note" />
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => { setShowCounter(false); setCounterAmount(""); setCounterNote(""); }} disabled={busy}
                className="btn-ghost text-sm" data-testid="counter-cancel-btn">Cancelar</button>
              <button onClick={() => call("resolve-response", { action: "counter", counter_amount: parseAmt(counterAmount), counter_note: counterNote })} disabled={busy}
                className="btn-primary text-sm disabled:opacity-50" data-testid="counter-send-btn">
                {busy ? "Enviando..." : "Enviar contraproposta"}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Sem proposta → oferece propor um acordo (com ou sem valor)
  return (
    <div className="aj-card p-4 space-y-2" data-testid="resolution-propose">
      <div className="flex items-center gap-2 font-bold text-sm"><CheckCircle size={18} weight="bold" className="text-emerald-600" /> Propor um acordo</div>
      <p className="text-xs text-slate-500">
        Proponha encerrar a disputa. Você pode oferecer/pedir um <b>valor</b> (o locatário paga o locador) ou encerrar <b>sem valor</b>. A outra parte pode aceitar, contrapropor ou recusar.
      </p>
      <NegotiationThread history={history} d={d} />
      <input type="number" step="0.01" min="0" inputMode="decimal" value={amount}
        onChange={(e) => setAmount(e.target.value)} placeholder="Valor do acordo (R$) — deixe 0 para sem valor"
        className="aj-input" data-testid="resolution-amount" />
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
        placeholder="Mensagem para a outra parte (opcional)" className="aj-input text-sm" data-testid="resolution-note" />
      <button onClick={() => call("resolve-proposal", { note, amount: parseAmt(amount) })} disabled={busy}
        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl py-2.5 disabled:opacity-50" data-testid="resolve-dispute-btn">
        {busy ? "Enviando..." : "Enviar proposta de acordo"}
      </button>
    </div>
  );
}

// Locatário paga o valor do acordo (PIX / Cartão / Saldo)
function SettlementPay({ d, id, reload }) {
  const [pm, setPm] = useState("pix");
  const [fees, setFees] = useState(null);
  const [showCard, setShowCard] = useState(false);
  const [loading, setLoading] = useState(false);
  const amount = Number(d.settlement?.amount || 0);

  useEffect(() => {
    api.get(`/disputes/${id}/settlement-fees`, { params: { payment_method: pm } })
      .then((r) => setFees(r.data)).catch(() => setFees(null));
  }, [pm, id]);

  const pay = async (cardData = {}) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/disputes/${id}/settlement-pay`, {
        payment_method: pm, origin_url: window.location.origin, client_ip: await getClientIp(), ...cardData,
      });
      if (data.paid) { toast.success(pm === "caucao" ? "Acordo debitado da caução! Troco estornado ao seu cartão." : "Acordo pago! Valor creditado ao locador."); reload(); return "paid"; }
      toast.info(pm === "pix" ? "Escaneie o PIX para concluir o pagamento." : "Pagamento em processamento.");
      reload();
      return "pending";
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return "error"; }
    finally { setLoading(false); }
  };

  return (
    <div className="aj-card p-4 space-y-3" data-testid="phase-settlement-pay">
      <div className="font-bold text-sm">Pague o valor acordado</div>
      <div className="rounded-xl bg-[#004E98]/5 border border-[#004E98]/20 p-3 text-sm flex items-center justify-between">
        <span className="text-slate-600">Acordo aceito</span>
        <b className="font-heading text-lg text-[#004E98]" data-testid="settlement-amount">{formatBRL(amount)}</b>
      </div>
      {d.settlement?.note && <p className="text-xs italic text-slate-500">"{d.settlement.note}"</p>}
      {/* V17: caução retida disponível → melhor opção (sem taxas extras p/ locatário) */}
      {fees?.caucao_ok && pm !== "caucao" && (
        <button onClick={() => { setPm("caucao"); setShowCard(false); }}
          className="w-full rounded-xl border-2 border-amber-400 bg-amber-50 p-3 text-left hover:bg-amber-100 transition-colors"
          data-testid="settlement-pm-caucao">
          <div className="text-xs font-black text-amber-900">🛡 Usar a caução retida ({formatBRL(fees.caucao_available)})</div>
          <div className="text-[11px] text-amber-800 mt-0.5">Debita o acordo direto da caução do seu cartão e estorna o troco automaticamente. Sem taxas extras para você.</div>
        </button>
      )}
      {pm === "caucao" && (
        <div className="rounded-xl border-2 border-amber-400 bg-amber-50 p-3 text-[11px] text-amber-900" data-testid="settlement-caucao-selected">
          <b>Pagamento via caução selecionado.</b> O valor do acordo será debitado da caução retida
          {fees?.caucao_available ? ` (${formatBRL(fees.caucao_available)})` : ""} e o troco volta para o seu cartão.
        </div>
      )}
      <div className="grid grid-cols-3 gap-2">
        {["pix", "card", "wallet"].map((m) => (
          <button key={m} onClick={() => { setPm(m); setShowCard(false); }}
            className={`p-2 rounded-xl border-2 text-xs font-bold ${pm === m ? "border-[#004E98] bg-[#004E98]/5" : "border-slate-200"}`}
            data-testid={`settlement-pm-${m}`}>
            {m === "pix" ? "PIX" : m === "card" ? "Cartão" : "Saldo"}
          </button>
        ))}
      </div>
      {fees && (
        <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm space-y-1" data-testid="settlement-fees">
          <div className="flex justify-between"><span className="text-slate-600">{pm === "caucao" ? "Valor debitado da caução" : "Valor (vai 100% ao locador)"}</span><b>{formatBRL(fees.quote_value)}</b></div>
          {pm === "caucao" ? (
            <div className="flex justify-between text-[11px]"><span className="text-slate-500">Taxa de movimentação da caução (paga pelo locador)</span><b>{formatBRL(fees.alugaja_fee)}</b></div>
          ) : (
            <div className="flex justify-between"><span className="text-slate-600">Taxa AlugaJá</span><b>{formatBRL(fees.alugaja_fee)}</b></div>
          )}
          {fees.asaas_fee > 0 && <div className="flex justify-between"><span className="text-slate-600">Taxa do gateway (Asaas)</span><b>{formatBRL(fees.asaas_fee)}</b></div>}
          <div className="flex justify-between font-heading font-black border-t border-slate-200 pt-1 mt-1"><span>{pm === "caucao" ? "Total debitado da caução" : "Total a pagar"}</span><span className="text-[#004E98]" data-testid="settlement-total">{formatBRL(fees.total_charged)}</span></div>
        </div>
      )}
      {pm === "card" && showCard ? (
        <AsaasCardForm amount={fees?.total_charged || 0} submitting={loading} showInstallments={false}
          onSubmit={async (cardData) => { const r = await pay(cardData); if (r === "paid") setShowCard(false); }} />
      ) : (
        <button onClick={() => pm === "card" ? setShowCard(true) : pay()} disabled={loading}
          className="btn-primary w-full disabled:opacity-50" data-testid="settlement-pay-btn">
          {loading ? "Processando..." : `Pagar ${fees ? formatBRL(fees.total_charged) : ""}`}
        </button>
      )}
    </div>
  );
}

// Dono envia 3 orçamentos
function QuotesForm({ id, reload, deadline }) {
  const [quotes, setQuotes] = useState([{ value: "", description: "", file_url: null }, { value: "", description: "", file_url: null }, { value: "", description: "", file_url: null }]);
  const [busy, setBusy] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(-1);
  const [sheetIdx, setSheetIdx] = useState(-1);

  const uploadFile = async (idx, file) => {
    if (!file) return;
    setUploadingIdx(idx);
    try {
      const data = await processAndUploadImage(file);
      setQuotes((q) => q.map((it, i) => i === idx ? { ...it, file_url: data.path } : it));
    } catch (err) { toast.error(err.message || formatApiError(err.response?.data?.detail)); }
    finally { setUploadingIdx(-1); }
  };
  const upload = (idx, e) => uploadFile(idx, e.target.files?.[0]);

  const submit = async () => {
    for (const q of quotes) { if (!q.value || parseFloat(q.value) <= 0) return toast.warning("Preencha o valor dos 3 orçamentos."); }
    setBusy(true);
    try {
      await api.post(`/disputes/${id}/quotes`, { quotes: quotes.map((q) => ({ value: parseFloat(q.value), description: q.description, file_url: q.file_url })) });
      toast.success("Orçamentos enviados ao locatário.");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="aj-card p-4 space-y-3" data-testid="phase-quotes-form">
      <div className="font-bold text-sm">Envie 3 orçamentos de conserto</div>
      <p className="text-xs text-slate-500">Anexe o PDF/foto de cada orçamento e informe o valor. O locatário escolherá um para pagar.</p>
      <DeadlineNote deadline={deadline} label="Prazo para enviar" />
      {quotes.map((q, i) => (
        <div key={i} className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`quote-input-${i}`}>
          <div className="text-xs font-bold text-slate-500">Orçamento {i + 1}</div>
          <input
            type="number" step="0.01" min="0" placeholder="Valor (R$)" value={q.value}
            onChange={(e) => setQuotes((qs) => qs.map((it, idx) => idx === i ? { ...it, value: e.target.value } : it))}
            className="aj-input" data-testid={`quote-value-${i}`} />
          <input
            type="text" placeholder="Descrição / fornecedor (opcional)" value={q.description}
            onChange={(e) => setQuotes((qs) => qs.map((it, idx) => idx === i ? { ...it, description: e.target.value } : it))}
            className="aj-input" />
          <label className="flex items-center gap-2 text-xs font-bold text-[#004E98] cursor-pointer">
            <UploadSimple size={16} weight="bold" />
            {q.file_url ? "Arquivo anexado ✓" : (uploadingIdx === i ? "Enviando..." : "Anexar PDF/foto")}
            <input type="file" accept="image/*,application/pdf" className="hidden" onClick={(e) => { if (isNative()) { e.preventDefault(); setSheetIdx(i); } }} onChange={(e) => upload(i, e)} data-testid={`quote-file-${i}`} />
          </label>
        </div>
      ))}
      <PhotoSourceSheet
        open={sheetIdx >= 0}
        allowFile
        onClose={() => setSheetIdx(-1)}
        onPick={(files) => { if (files?.[0]) uploadFile(sheetIdx, files[0]); }}
      />
      <button onClick={submit} disabled={busy} className="btn-primary w-full disabled:opacity-50" data-testid="submit-quotes-btn">
        {busy ? "Enviando..." : "Enviar orçamentos"}
      </button>
    </div>
  );
}

// Locatário escolhe orçamento e paga
function ChooseAndPay({ d, id, reload }) {
  const [selected, setSelected] = useState(null);
  const [pm, setPm] = useState("pix");
  const [fees, setFees] = useState(null);
  const [showCard, setShowCard] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selected == null) { setFees(null); return; }
    api.get(`/disputes/${id}/quote-fees`, { params: { quote_index: selected, payment_method: pm } })
      .then((r) => setFees(r.data)).catch(() => setFees(null));
  }, [selected, pm, id]);

  const pay = async (cardData = {}) => {
    if (selected == null) return toast.warning("Escolha um orçamento.");
    setLoading(true);
    try {
      const { data } = await api.post(`/disputes/${id}/choose-quote`, {
        quote_index: selected, payment_method: pm, origin_url: window.location.origin, client_ip: await getClientIp(), ...cardData,
      });
      if (data.paid) { toast.success("Conserto pago! Valor creditado ao dono."); reload(); return "paid"; }
      // PIX / cartão pendente → recarrega para a fase de pagamento
      toast.info(pm === "pix" ? "Escaneie o PIX para concluir o pagamento." : "Pagamento em processamento.");
      reload();
      return "pending";
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return "error"; }
    finally { setLoading(false); }
  };

  return (
    <div className="aj-card p-4 space-y-3" data-testid="phase-choose-pay">
      <div className="font-bold text-sm">Escolha um orçamento e pague o conserto</div>
      <div className="space-y-2">
        {(d.quotes || []).map((q, i) => (
          <button key={i} onClick={() => setSelected(i)}
            className={`w-full text-left rounded-xl border-2 p-3 transition-colors ${selected === i ? "border-[#004E98] bg-[#004E98]/5" : "border-slate-200"}`}
            data-testid={`quote-card-${i}`}>
            <div className="flex items-center justify-between">
              <div className="font-heading font-black text-base">{formatBRL(q.value)}</div>
              {q.file_url && <a href={fileUrl(q.file_url)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-xs text-[#004E98] font-bold flex items-center gap-1"><FileText size={14} /> Ver orçamento</a>}
            </div>
            {q.description && <div className="text-xs text-slate-500 mt-0.5">{q.description}</div>}
          </button>
        ))}
      </div>

      {selected != null && (
        <>
          <div className="grid grid-cols-3 gap-2 pt-1">
            {["pix", "card", "wallet"].map((m) => (
              <button key={m} onClick={() => { setPm(m); setShowCard(false); }}
                className={`p-2 rounded-xl border-2 text-xs font-bold ${pm === m ? "border-[#004E98] bg-[#004E98]/5" : "border-slate-200"}`}
                data-testid={`repair-pm-${m}`}>
                {m === "pix" ? "PIX" : m === "card" ? "Cartão" : "Saldo"}
              </button>
            ))}
          </div>
          {fees && (
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm space-y-1" data-testid="repair-fees">
              <div className="flex justify-between"><span className="text-slate-600">Orçamento (vai 100% ao dono)</span><b>{formatBRL(fees.quote_value)}</b></div>
              <div className="flex justify-between"><span className="text-slate-600">Taxa AlugaJá</span><b>{formatBRL(fees.alugaja_fee)}</b></div>
              {fees.asaas_fee > 0 && <div className="flex justify-between"><span className="text-slate-600">Taxa do gateway (Asaas)</span><b>{formatBRL(fees.asaas_fee)}</b></div>}
              <div className="flex justify-between font-heading font-black border-t border-slate-200 pt-1 mt-1"><span>Total a pagar</span><span className="text-[#004E98]" data-testid="repair-total">{formatBRL(fees.total_charged)}</span></div>
            </div>
          )}
          {pm === "card" && showCard ? (
            <AsaasCardForm amount={fees?.total_charged || 0} submitting={loading} showInstallments={false}
              onSubmit={async (cardData) => { const r = await pay(cardData); if (r === "paid") setShowCard(false); }} />
          ) : (
            <button onClick={() => pm === "card" ? setShowCard(true) : pay()} disabled={loading}
              className="btn-primary w-full disabled:opacity-50" data-testid="approve-pay-btn">
              {loading ? "Processando..." : `Aprovar e Pagar ${fees ? formatBRL(fees.total_charged) : ""}`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

// Pagamento em andamento (PIX poll / cartão)
function RepairPayment({ d, id, reload }) {
  const repair = d.repair || {};
  useEffect(() => {
    if (repair.status === "paid") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/disputes/${id}/repair-status`);
        if (data.status === "paid") { clearInterval(t); toast.success("Pagamento confirmado!"); reload(); }
      } catch { /* ignore */ }
    }, 5000);
    return () => clearInterval(t);
  }, [id, repair.status, reload]);

  return (
    <div className="aj-card p-4 space-y-3" data-testid="phase-repair-payment">
      <div className="flex items-center gap-2 font-bold text-sm"><Spinner size={18} className="animate-spin text-[#004E98]" /> Aguardando confirmação do pagamento</div>
      <DeadlineNote deadline={d.payment_deadline} label="Pague até" />
      <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
        <Warning size={12} weight="fill" className="inline mr-1" />
        Pague dentro do prazo. Reconhecer o dano e não pagar bloqueia as funções do seu app e registra inadimplência.
      </div>
      {repair.qr_code_base64 && (
        <div className="text-center" data-testid="repair-pix-qr">
          <img src={`data:image/png;base64,${repair.qr_code_base64}`} alt="PIX" className="w-48 h-48 mx-auto" />
          {repair.qr_code && (
            <button onClick={() => { navigator.clipboard.writeText(repair.qr_code); toast.success("Código PIX copiado"); }}
              className="btn-ghost text-xs mt-2" data-testid="repair-copy-pix">Copiar código PIX</button>
          )}
        </div>
      )}
      <div className="text-sm text-slate-600">Total: <b>{formatBRL(repair.total_charged)}</b></div>
    </div>
  );
}

// Dossiê de B.O. (não devolução)
function BoDossier({ id }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const generate = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/disputes/${id}/bo-dossie`); setData(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  if (!data) {
    return (
      <div className="aj-card p-4 space-y-2" data-testid="phase-bo">
        <div className="flex items-center gap-2 font-black text-purple-700"><Gavel size={20} weight="fill" /> Não-devolução aprovada</div>
        <p className="text-sm text-slate-600">O AlugaJá aprovou o caso. Gere o Dossiê de B.O. com os dados do locatário e o amparo legal para registro da ocorrência.</p>
        <button onClick={generate} disabled={loading} className="btn-primary w-full disabled:opacity-50" data-testid="generate-bo-btn">
          <IdentificationCard size={18} weight="bold" className="inline mr-1" /> {loading ? "Gerando..." : "Gerar Dossiê de B.O."}
        </button>
      </div>
    );
  }
  const r = data.renter || {};
  return (
    <div className="aj-card p-4 space-y-3" data-testid="bo-dossier">
      <div className="font-black text-purple-700 flex items-center gap-2"><IdentificationCard size={20} weight="fill" /> Dossiê de B.O.</div>
      <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm space-y-1">
        <Row label="Nome" value={r.name} />
        <Row label={r.doc_type === "cnpj" ? "CNPJ" : "CPF"} value={r.cpf} />
        <Row label="Telefone" value={r.phone} />
        <Row label="E-mail" value={r.email} />
        <Row label="Endereço" value={typeof r.address === "string" ? r.address : (r.address ? `${r.address.street || ""} ${r.address.number || ""}, ${r.address.city || ""}` : "—")} />
      </div>
      <div className="rounded-xl border-2 border-purple-200 bg-purple-50 p-3 text-[11px] leading-relaxed text-purple-900" data-testid="bo-legal">
        <b>{data.article}</b><br />{data.legal_text}
      </div>
      <button onClick={() => window.print()} className="btn-ghost w-full text-sm" data-testid="bo-print">Imprimir / Salvar dossiê</button>
    </div>
  );
}
function Row({ label, value }) {
  return <div className="flex justify-between gap-3"><span className="text-slate-500">{label}</span><b className="text-right">{value || "—"}</b></div>;
}

export default function DisputeCenter() {
  const { id } = useParams();
  return id ? <DisputeDetail id={id} /> : <DisputeList />;
}
